// Reads the answer of an upload request (UP-07). The API answers JSON, but a
// proxy may answer first: nginx's 413 "Request Entity Too Large" page is
// HTML, and `await response.json()` on it used to surface as
// "Unexpected token '<' … is not valid JSON".
import { formatMb, UPLOAD_LIMITS } from "./uploadPolicy";

const STATUS_MESSAGES = {
  400: "La requête d'envoi est invalide. Réessayez.",
  401: "Votre session a expiré. Reconnectez-vous puis réessayez.",
  403: "Vous n'avez pas l'autorisation d'envoyer ce fichier.",
  404: "Service d'envoi introuvable.",
  413: null, // built with the limit below
  415: "Format de fichier non accepté.",
  422: "Le fichier n'est pas valide ou il est endommagé.",
  429: "Trop d'envois en peu de temps. Patientez un instant puis réessayez.",
  500: "L'envoi du fichier a échoué sur le serveur. Réessayez.",
  502: "Le serveur est momentanément injoignable. Réessayez dans un instant.",
  503: "Le serveur est occupé. Réessayez dans quelques secondes.",
  504: "L'envoi a pris trop de temps. Vérifiez votre connexion et réessayez.",
};

export class UploadRequestError extends Error {
  constructor(message, { status, code, data } = {}) {
    super(message);
    this.name = "UploadRequestError";
    this.status = status;
    this.code = code;
    this.data = data;
    this.retryable = Boolean(data?.retryable) || [502, 503, 504].includes(status);
  }
}

const tooLargeMessage = (data) => {
  const limit = data?.maxBytes || data?.maxBytesProxy;
  return limit
    ? `Fichier trop volumineux. Taille maximale : ${formatMb(limit)} Mo.`
    : `Fichier trop volumineux pour être envoyé (limite du serveur : ${formatMb(UPLOAD_LIMITS.proxy.defaultRequestBytes)} Mo par envoi).`;
};

export const messageForStatus = (status, data) => (status === 413 ? tooLargeMessage(data) : STATUS_MESSAGES[status] || `Réponse inattendue du serveur (HTTP ${status}).`);

// Resolves the parsed JSON of a successful answer; throws UploadRequestError
// (French message, HTTP status, stable code) otherwise.
export const readUploadResponse = async (response) => {
  let data = null;
  if (typeof response.text === "function") {
    const text = await response.text().catch(() => "");
    try { data = text ? JSON.parse(text) : {}; } catch (_) { data = null; }
  } else if (typeof response.json === "function") {
    data = await response.json().catch(() => null);
  }
  if (response.ok && data && data.success !== false) return data;
  const status = response.status;
  if (!data) {
    throw new UploadRequestError(messageForStatus(status), { status, code: status === 413 ? "REQUEST_TOO_LARGE" : "UNEXPECTED_RESPONSE" });
  }
  const message = status === 413 && !data.error ? tooLargeMessage(data) : (data.error || data.message || messageForStatus(status, data));
  throw new UploadRequestError(message, { status, code: data.code || (status === 413 ? "REQUEST_TOO_LARGE" : undefined), data });
};

const wait = (ms) => new Promise((resolve) => setTimeout(resolve, ms));

// Retries a busy server (503 UPLOAD_BUSY / network blip) a couple of times.
export const withUploadRetry = async (send, { attempts = 3, delayMs = 1500 } = {}) => {
  let lastError;
  for (let attempt = 1; attempt <= attempts; attempt += 1) {
    try { return await send(); } catch (error) {
      if (error?.name === "TypeError" && !error.status) {
        throw Object.assign(new Error("Connexion interrompue pendant l'envoi. Vérifiez votre connexion ou réduisez la taille du fichier, puis réessayez."), { code: "NETWORK_ERROR", cause: error });
      }
      lastError = error;
      // Only a busy server is retried. A network error ("Failed to fetch") is
      // not: behind nginx it is often a too-large body whose connection was
      // closed, and resending it would only waste the user's bandwidth.
      const retryable = error?.retryable || error?.status === 503;
      if (!retryable || attempt === attempts) throw error;
      await wait(delayMs * attempt);
    }
  }
  throw lastError;
};

// Runs uploads one after the other (the server accepts a few concurrent
// uploads per process; parallel bursts used to get 503 "Uploads are busy").
export const runSequentially = async (items, task) => {
  const results = [];
  for (const item of items) results.push(await task(item));
  return results;
};

// Same as readUploadResponse, but never throws: any JSON answer is returned
// as before (including { success:false } / { error } bodies), and a non-JSON
// or failed answer becomes { success:false, error, errors, code, status }.
// For the historical api*.js helpers whose callers test `data.error`.
export const readJsonOrUploadError = async (response) => {
  try { return await readUploadResponse(response); } catch (error) { return uploadErrorResult(error); }
};

export const uploadErrorResult = (error) => error?.data || {
  success: false,
  error: error?.message || "L'envoi du fichier a échoué.",
  code: error?.code,
  status: error?.status,
  errors: [{ code: error?.code || "UPLOAD_FAILED", message: error?.message || "L'envoi du fichier a échoué." }],
};
